import { ChevronDownOutline, ChevronUpOutline } from '@aurora/icons-react';
import { Button, ButtonGroup, Popup, Toolbar } from '@aurora/widgets-react';
import clsx from 'clsx';
import { compute as computeScrollIntoView } from 'compute-scroll-into-view';
import { useSelect, UseSelectStateChange } from 'downshift';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TimedGroup } from '../MapViewer/TimedGroup';
import styles from './LayerPicker.scss';

export interface LayerPickerProps {
  setLayer: (value: TimedGroup) => void;
  currentLayer: TimedGroup;
  layers: TimedGroup[];
  layout: 's' | 'm';
}

const InnerLayerPicker: React.FunctionComponent<LayerPickerProps> = ({
  setLayer,
  currentLayer,
  layers,
  layout,
}) => {
  const { t } = useTranslation();
  const scrollIntoView = useCallback((node: HTMLElement, _: HTMLElement) => {
    const actions = computeScrollIntoView(node, {
      scrollMode: 'if-needed',
      block: 'nearest',
    });
    actions.forEach(({ el, top, left }) => {
      el.scrollTop = top;
      el.scrollLeft = left;
    });
  }, []);

  const onSelectedItemChange = useCallback(
    (changes: UseSelectStateChange<TimedGroup>) => {
      if (!changes.selectedItem) return;
      setLayer(changes.selectedItem);
    },
    [setLayer]
  );

  const itemToString = useCallback((item: TimedGroup) => t(item.key), [t]);

  const {
    closeMenu,
    isOpen,
    getLabelProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
    getToggleButtonProps,
  } = useSelect({
    items: layers,
    itemToString,
    selectedItem: currentLayer,
    scrollIntoView,
    onSelectedItemChange,
  });

  return (
    <Toolbar floating>
      <ButtonGroup className={clsx(styles.layerPicker, styles[layout])}>
        <label className="sr-only" {...getLabelProps()}>
          {t('chooseLayer')}
        </label>
        <Button
          iconPosition="right"
          variant="secondary"
          icon={
            isOpen ? (
              <ChevronUpOutline size="s" />
            ) : (
              <ChevronDownOutline size="s" />
            )
          }
          {...getToggleButtonProps({
            className: styles.button,
            onBlur: (event) => {
              event.preventDownshiftDefault = true;
            },
          })}
          label={layout === 's' ? itemToString(currentLayer) : t('chooseLayer')}
        />
        {layout === 's' ? null : (
          <div className={styles.currentLayer}>
            {itemToString(currentLayer)}
          </div>
        )}
        <Popup
          arrow="topLeft"
          className={clsx(!isOpen && styles.hidden, styles.menu)}
        >
          <ul {...getMenuProps()}>
            {layers.map((item, index) => (
              <li
                key={index}
                {...getItemProps({
                  item,
                  index,
                  className: clsx(
                    styles.menuItem,
                    index === highlightedIndex && styles.highlighted,
                    item === currentLayer && styles.selected
                  ),
                })}
              >
                {itemToString(item)}
              </li>
            ))}
          </ul>
        </Popup>
      </ButtonGroup>
    </Toolbar>
  );
};

export const LayerPicker = React.memo(InnerLayerPicker);
